import React, { useEffect, useState } from 'react'
import Header from './includes/Header'
import Footer from './includes/Footer'
import { useNavigate, useParams } from 'react-router-dom'
import Helpers from '../../Helpers/Helpers'
import Breadcrum from '../components/Breadcrum'
import { useProfile } from '../context/ProfileContext'
import Notification from '../../Helpers/Notification'
import { clearCart } from '../../redux/actions/cartActions'
import { useDispatch } from 'react-redux'

const RechargePlanDetails = ({ title }) => {
    const dispatch = useDispatch()
    const { id } = useParams()
    const [loading, setLoading] = useState(false)
    const [creatingPayment, setCreatingPayment] = useState(false)
    const [paymentStatus, setPaymentStatus] = useState('')
    const helpers = Helpers()
    const [plan, setPlan] = useState({})
    const { userInfo, setUserInfo } = useProfile()
    const navigate = useNavigate()

    useEffect(() => {
        document.title = title
    }, [title])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const response = await helpers.httpRequest(`/recharge-plan/${id}`, 'GET')
                console.log('Plan response:', response);

                if (response.status === 'success') {
                    // Backend spreads plan data directly into response object, not under 'data' property
                    setPlan(response)
                }
            } catch (error) {
                console.error('Error fetching plan:', error)
                Notification('error', 'Failed to load plan details')
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [])

    // Check payment status after redirect from PhonePe
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const orderId = urlParams.get('orderId')
        const statusParam = urlParams.get('status')

        if (orderId && statusParam === 'redirect') {
            // Clear the URL params to avoid confusion
            window.history.replaceState({}, document.title, window.location.pathname)
            
            // 💡 Direct API approach: Simply check payment status
            checkPaymentStatus(orderId)
        }
    }, [])

    const handlePayment = async () => {
        if (!id) {
            Notification('error', 'Invalid plan selected')
            return
        }

        setCreatingPayment(true)
        try {
            // Using the new PhonePe SDK endpoint
            const response = await helpers.httpRequest('/recharge/initiate', 'POST', { planId: id })
            console.log('Payment initiation response:', response);

            if (response.status === 'success') {
                // Backend spreads data directly into response object, not under 'data' property
                const { paymentUrl, orderId, merchantOrderId } = response
                
                // Store order info for status checking after redirect
                helpers.setStorage('currentOrder', { orderId, merchantOrderId, planId: id })
                
                // 💡 Direct approach: Just redirect to PhonePe
                // When user returns, webhook will have updated the database
                window.location.href = paymentUrl
            } else {
                Notification('error', response.message || 'Failed to initiate payment')
            }
        } catch (error) {
            console.error('Error initiating payment:', error)
            Notification('error', 'Failed to initiate payment')
        } finally {
            setCreatingPayment(false)
        }
    }

    const checkPaymentStatus = async (orderId) => {
        try {
            setPaymentStatus('checking')
            const response = await helpers.httpRequest(`/recharge/status/${orderId}`, 'GET')
            console.log('💡 Payment status response:', response);

            if (response.status === 'success') {
                // Backend spreads data directly into response object, not under 'data' property
                const { status, amount, baseAmount } = response
                setPaymentStatus(status)

                if (status === 'SUCCESS') {
                    // 🎉 Webhook already updated wallet balance!
                    // Just refresh user data to reflect the change
                    await helpers.refreshUserData()
                    
                    // Clear any stored order info
                    helpers.deleteStorage('currentOrder')
                    
                    // Clear cart and navigate to success page
                    dispatch(clearCart())
                    Notification('success', `Payment successful! ₹${baseAmount} added to wallet`)
                    navigate('/recharge-success')
                    
                } else if (status === 'FAILED') {
                    Notification('error', 'Payment failed. Please try again.')
                    helpers.deleteStorage('currentOrder')
                } else {
                    // Still pending - show verify button
                    Notification('info', 'Payment is being processed...')
                }
            } else {
                Notification('error', response.message || 'Failed to check payment status')
            }
        } catch (error) {
            console.error('Error checking payment status:', error)
            Notification('error', 'Failed to verify payment status')
        }
    }

    const verifyPayment = async () => {
        const orderInfo = helpers.getStorage('currentOrder')
        if (!orderInfo) {
            Notification('error', 'No payment found to verify')
            return
        }

        try {
            setPaymentStatus('verifying')
            const response = await helpers.httpRequest('/recharge/verify', 'POST', {
                orderId: orderInfo.orderId,
                paymentId: orderInfo.merchantOrderId, // Using merchantOrderId as paymentId
                signature: '' // PhonePe SDK handles signature verification on backend
            })

            if (response.status === 'success') {
                // Backend spreads data directly into response object, not under 'data' property
                const { status, amount } = response
                
                if (status === 'SUCCESS') {
                    await helpers.refreshUserData()
                    helpers.deleteStorage('currentOrder')
                    dispatch(clearCart())
                    Notification('success', `Payment verified! ₹${amount} added to wallet`)
                    navigate('/recharge-success')
                } else {
                    Notification('error', 'Payment verification failed')
                    setPaymentStatus('FAILED')
                }
            } else {
                Notification('error', response.message || 'Payment verification failed')
                setPaymentStatus('FAILED')
            }
        } catch (error) {
            console.error('Error verifying payment:', error)
            Notification('error', 'Failed to verify payment')
            setPaymentStatus('FAILED')
        }
    }

    const totalAmount = plan?.amount ? (
        Number(plan.amount) + (Number(plan.amount) * 18) / 100
    ).toFixed(2) : '0.00'

    return (
        <>
            <div className="as_main_wrapper">
                <Header />
                <Breadcrum title={title} />
                <section className="as_shop_wrapper as_padderBottom90 as_padderTop80">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12 col-md-8 col-sm-12 col-xs-12" style={{ display: 'flex', justifyContent: 'center', color: 'black' }}>
                                <h1>Plan Details</h1>
                            </div>
                            
                            {loading ? (
                                <div className="col-lg-12 text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="col-lg-12">
                                        <table className='table table-bordered'>
                                            <tbody>
                                                <tr>
                                                    <th>Recharge Amount</th>
                                                    <td>₹ {plan?.amount || '0'}</td>
                                                </tr>
                                                <tr>
                                                    <th>GST @18%</th>
                                                    <td>₹ {plan?.amount ? ((Number(plan.amount) * 18) / 100).toFixed(2) : '0.00'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Total Amount</th>
                                                    <td>₹ {totalAmount}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="col-lg-12 text-center">
                                        <button
                                            className='btn btn-primary btn-lg me-3'
                                            onClick={handlePayment}
                                            disabled={creatingPayment || !plan?.amount}
                                            style={{ marginRight: '10px' }}
                                        >
                                            {creatingPayment ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <img 
                                                        src="https://www.phonepe.com/img/phonepe-logo.svg" 
                                                        alt="PhonePe" 
                                                        style={{ width: '20px', height: '20px', marginRight: '8px' }}
                                                        onError={(e) => { e.target.style.display = 'none' }}
                                                    />
                                                    Pay ₹{totalAmount} with PhonePe
                                                </>
                                            )}
                                        </button>

                                        {paymentStatus === 'checking' && (
                                            <div className="mt-3">
                                                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                                                <span>Checking payment status...</span>
                                            </div>
                                        )}

                                        {paymentStatus === 'verifying' && (
                                            <div className="mt-3">
                                                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                                                <span>Verifying payment...</span>
                                            </div>
                                        )}

                                        {paymentStatus === 'PENDING' && (
                                            <div className="mt-3">
                                                <button
                                                    className='btn btn-info'
                                                    onClick={verifyPayment}
                                                    disabled={paymentStatus === 'verifying'}
                                                >
                                                    Verify Payment
                                                </button>
                                                <small className="d-block mt-2 text-muted">
                                                    Click here if payment was completed but not updated
                                                </small>
                                            </div>
                                        )}

                                        {paymentStatus === 'FAILED' && (
                                            <div className="mt-3">
                                                <p className="text-danger">Payment failed. Please try again.</p>
                                                <button
                                                    className='btn btn-primary'
                                                    onClick={handlePayment}
                                                    disabled={creatingPayment}
                                                >
                                                    Retry Payment
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </section>
                <Footer />
            </div>
        </>
    )
}

export default RechargePlanDetails
